import { ref } from 'vue'

export interface SearchContact {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  type: 'contact'
  displayName: string
}

export interface SearchBusiness {
  id: string
  name: string
  type: 'business'
  displayName: string
}

export interface SearchResults {
  contacts: SearchContact[]
  businesses: SearchBusiness[]
  total: number
}

export function useUniversalSearch() {
  const searchResults = ref<SearchResults>({
    contacts: [],
    businesses: [],
    total: 0,
  })
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  let debounceTimer: NodeJS.Timeout | null = null

  const search = async (query: string, debounceMs: number = 400) => {
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // Reset results if query is too short
    if (!query || query.trim().length < 2) {
      searchResults.value = {
        contacts: [],
        businesses: [],
        total: 0,
      }
      error.value = null
      return
    }

    // Debounce the API call
    return new Promise<void>((resolve) => {
      debounceTimer = setTimeout(async () => {
        try {
          isLoading.value = true
          error.value = null

          const response = await $fetch<{ success: boolean; data: SearchResults }>(
            '/api/v1/search',
            {
              method: 'GET',
              params: {
                q: query.trim(),
              },
            },
          )

          if (response.success && response.data) {
            searchResults.value = response.data
          }
          else {
            searchResults.value = {
              contacts: [],
              businesses: [],
              total: 0,
            }
          }
        }
        catch (err: any) {
          console.error('Search error:', err)
          error.value = err.message || 'Failed to perform search'
          searchResults.value = {
            contacts: [],
            businesses: [],
            total: 0,
          }
        }
        finally {
          isLoading.value = false
          resolve()
        }
      }, debounceMs)
    })
  }

  const clearResults = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    searchResults.value = {
      contacts: [],
      businesses: [],
      total: 0,
    }
    error.value = null
    isLoading.value = false
  }

  return {
    searchResults,
    isLoading,
    error,
    search,
    clearResults,
  }
}

